import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, Globe } from "lucide-react";
import { toast } from "sonner";
import QuestionRenderer from "./survey/QuestionRenderer";
import { useCascadingFilters } from "./survey/useCascadingFilters";
import type { Question } from "./survey/types";

interface SurveyFormProps {
  title: string;
  description?: string | null;
  questions: Question[];
  onSubmit: (answers: Record<string, any>) => Promise<void>;
  isSubmitting?: boolean;
  titleSw?: string | null;
  descriptionSw?: string | null;
  hasSwahili?: boolean;
  releasedBy?: string | null;
  inquiryPhone?: string | null;
  publishedAt?: string | null;
}

const formatDateOrdinal = (dateStr: string) => {
  const d = new Date(dateStr);
  const day = d.getDate();
  const suffix = [11, 12, 13].includes(day % 100)
    ? "th"
    : { 1: "st", 2: "nd", 3: "rd" }[day % 10] || "th";
  const month = d.toLocaleString("en-US", { month: "long" });
  const year = d.getFullYear();
  return `${day}${suffix} ${month}, ${year}`;
};

const SurveyForm = ({ title, description, questions, onSubmit, isSubmitting, titleSw, descriptionSw, hasSwahili, releasedBy, inquiryPhone, publishedAt }: SurveyFormProps) => {
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [lang, setLang] = useState<"en" | "sw">("en");

  const {
    getChoiceOptions, isQuestionLoading, isWaitingForParent, getPlaceholderHint, moduleQuestionId,
  } = useCascadingFilters(questions, answers, setAnswers);

  const setAnswer = (questionId: string, value: any) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleMultiChoice = (questionId: string, option: string, checked: boolean) => {
    setAnswers(prev => {
      const current = (prev[questionId] as string[]) || [];
      return {
        ...prev,
        [questionId]: checked ? [...current, option] : current.filter(v => v !== option),
      };
    });
  };

  const handleGridChoice = (questionId: string, row: string, col: string, multi: boolean) => {
    setAnswers(prev => {
      const grid = (prev[questionId] as Record<string, string | string[]>) || {};
      if (multi) {
        const current = (grid[row] as string[]) || [];
        const updated = current.includes(col) ? current.filter(c => c !== col) : [...current, col];
        return { ...prev, [questionId]: { ...grid, [row]: updated } };
      }
      return { ...prev, [questionId]: { ...grid, [row]: col } };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const missing = questions.filter(q => {
      if (!q.is_required) return false;
      const a = answers[q.id];
      if (!a) return true;
      if (Array.isArray(a) && a.length === 0) return true;
      if (typeof a === "string" && a.trim() === "") return true;
      return false;
    });
    if (missing.length > 0) {
      toast.error(`Please fill in all required fields (${missing.map(m => m.question_text).join(", ")})`);
      return;
    }
    await onSubmit(answers);
  };

  const sorted = [...questions].sort((a, b) => a.sort_order - b.sort_order);

  // Aggregate selected modules from all module-source questions + cascading filter
  const selectedModules: string[] = (() => {
    const moduleQIds = new Set(
      questions
        .filter(q => typeof q.options === "object" && !Array.isArray(q.options) && (q.options as any)?.source === "module")
        .map(q => q.id)
    );
    // Also include the moduleQuestionId from cascading filters
    if (moduleQuestionId) moduleQIds.add(moduleQuestionId);
    // Also check any multiple_choice question whose current options match module data
    const all: string[] = [];
    for (const qId of moduleQIds) {
      const val = answers[qId];
      if (Array.isArray(val)) all.push(...val);
      else if (val) all.push(val as string);
    }
    return [...new Set(all)];
  })();

  // Check if any Swahili content exists
  const showLangToggle = hasSwahili || (titleSw || descriptionSw || questions.some(q => q.question_text_sw));

  const displayTitle = lang === "sw" && titleSw ? titleSw : title;
  const displayDesc = lang === "sw" && descriptionSw ? descriptionSw : description;

  const getQuestionText = (q: Question) => {
    if (lang === "sw" && q.question_text_sw) return q.question_text_sw;
    return q.question_text;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-2xl mx-auto">
      <div className="flex flex-col items-center gap-3 py-6">
        <div className="bg-white rounded-lg p-3 shadow-sm">
          <img
            src="/images/college-logo.png"
            alt="St. David College of Health Sciences"
            className="h-20 w-auto object-contain"
          />
        </div>
        <p className="text-sm font-medium text-muted-foreground">St. David College of Health Sciences</p>
      </div>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg sm:text-xl">{displayTitle}</CardTitle>
            {showLangToggle && (
              <div className="flex items-center gap-1 border rounded-lg p-0.5 bg-muted/50">
                <button
                  type="button"
                  onClick={() => setLang("en")}
                  className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${lang === "en" ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                >
                  EN
                </button>
                <button
                  type="button"
                  onClick={() => setLang("sw")}
                  className={`px-2.5 py-1 text-xs font-medium rounded transition-colors flex items-center gap-1 ${lang === "sw" ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                >
                  <Globe className="h-3 w-3" />
                  SW
                </button>
              </div>
            )}
          </div>
          {displayDesc && <CardDescription>{displayDesc}</CardDescription>}
        </CardHeader>
      </Card>

      {sorted.map((q) => (
        <Card key={q.id}>
          <CardContent className="pt-4 space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              {getQuestionText(q)}
              {q.is_required && <span className="text-destructive ml-1">*</span>}
              {isQuestionLoading(q.id) && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
            </Label>

            <QuestionRenderer
              question={q.question_type === "module_availability" ? { ...q, _selectedModules: selectedModules } as any : q}
              answer={answers[q.id]}
              onAnswer={(value) => setAnswer(q.id, value)}
              onMultiChoice={(option, checked) => handleMultiChoice(q.id, option, checked)}
              onGridChoice={(row, col, multi) => handleGridChoice(q.id, row, col, multi)}
              options={getChoiceOptions(q.options, q.id)}
              isLoading={isQuestionLoading(q.id)}
              isWaiting={isWaitingForParent(q.id)}
              placeholderHint={getPlaceholderHint(q.id)}
              lang={lang}
            />
          </CardContent>
        </Card>
      ))}

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? (lang === "sw" ? "Inatuma..." : "Submitting...") : (lang === "sw" ? "Tuma" : "Submit")}
      </Button>

      {releasedBy && (
        <div className="text-center space-y-1 pt-4 pb-2 text-sm text-muted-foreground">
          <p>Released by: <span className="font-medium">{releasedBy}</span></p>
          <p>St. David's College of Health and Allied Sciences — {publishedAt ? formatDateOrdinal(publishedAt) : formatDateOrdinal(new Date().toISOString())}</p>
          {inquiryPhone && <p>For further inquiry, please call <span className="font-medium">{inquiryPhone}</span></p>}
        </div>
      )}
    </form>
  );
};

export default SurveyForm;
